import { useState, useEffect, useRef, useCallback, useMemo, type ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MessageSquare,
  Search,
  Send,
  Plus,
  Loader2,
  ExternalLink,
  Paperclip,
  X,
  FileText,
  Film,
  Music,
  Image as ImageIcon,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { useGetIdentity } from '@refinedev/core';
import { cn } from '@/lib/utils';
import { resolveApiUrl } from '@/providers/api-url';
import { useAdminChatWs, type WsNewMessage, type WsRoomUpdateData } from '@/hooks/use-admin-chat-ws';
import { useScrollRestoration } from '@/hooks/use-scroll-restoration';
import { MessageDeliveryStatus } from '@masters-club/types';
import { AdminMessageBubble } from '@/components/chat/admin-message-bubble';

// ─── Types ───────────────────────────────────────────────────────────────────

interface RoomParticipantUser {
  id: number;
  firstName: string | null;
  lastName: string | null;
  username: string | null;
  photoUrl: string | null;
}

interface RoomParticipantAdmin {
  id: number;
  username: string;
  email: string;
}

interface RoomParticipant {
  userId: number | null;
  adminUserId: number | null;
  user: RoomParticipantUser | null;
  adminUser: RoomParticipantAdmin | null;
}

interface RoomLastMessageSender {
  type: 'user' | 'admin';
  id: number;
  firstName: string | null;
  lastName: string | null;
  username: string | null;
}

interface RoomLastMessage {
  id: number;
  content: string;
  createdAt: string;
  sender: RoomLastMessageSender | null;
}

interface ChatRoom {
  id: number;
  type: string;
  name: string | null;
  isActive: boolean;
  lastMessageAt: string | null;
  createdAt: string;
  unreadCount: number;
  participants: RoomParticipant[];
  lastMessage: RoomLastMessage | null;
}

interface MessageSender {
  type: 'user' | 'admin';
  id: number;
  firstName: string | null;
  lastName: string | null;
  username: string | null;
  photoUrl?: string | null;
  email?: string;
}

interface MessageReaction {
  emoji: string;
  count: number;
  hasReacted: boolean;
}

interface MessageReadBy {
  type: 'user' | 'admin';
  id: number;
  firstName: string | null;
  username: string | null;
  readAt: string;
}

interface ChatMessageAttachment {
  id: number;
  fileId: number;
  type: 'image' | 'video' | 'audio' | 'document';
  url: string;
  name: string;
  size: number;
  mimeType: string;
}

interface ChatMessage {
  id: number;
  chatRoomId: number;
  content: string;
  isSystemMessage: boolean;
  replyToMessageId: number | null;
  status: MessageDeliveryStatus;
  isEdited: boolean;
  editedAt: string | null;
  isDeleted: boolean;
  createdAt: string;
  sender: MessageSender | null;
  replyTo: ChatMessage | null;
  reactions: MessageReaction[];
  readBy: MessageReadBy[];
  attachments: ChatMessageAttachment[];
}

interface PendingAttachment {
  tempId: string;
  file: File;
  preview: string | null;
  uploadedFileId: number | null;
  isUploading: boolean;
  error: string | null;
}

interface ChatUploadResponseData {
  id: number;
  url: string;
  name: string;
  size: number;
  mimeType: string;
}

interface ChatUploadResponse {
  success: boolean;
  data: ChatUploadResponseData | null;
}

const MAX_ATTACHMENTS = 5;
const ACCEPTED_FILE_TYPES = 'image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip,.rar';
const MAX_CHAT_FILE_SIZE = 20 * 1024 * 1024;

function formatFileSize(bytes: number): string {
  if (bytes < 1024) { return `${bytes} B`; }
  if (bytes < 1024 * 1024) { return `${(bytes / 1024).toFixed(1)} KB`; }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getFileTypeFromMime(mimeType: string): 'image' | 'video' | 'audio' | 'document' {
  if (mimeType.startsWith('image/')) { return 'image'; }
  if (mimeType.startsWith('video/')) { return 'video'; }
  if (mimeType.startsWith('audio/')) { return 'audio'; }
  return 'document';
}

function generateTempId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

interface SearchUserResult {
  id: number;
  firstName: string | null;
  lastName: string | null;
  username: string | null;
  photoUrl: string | null;
}

interface TypingState {
  roomId: number;
  userName: string;
  timeoutId: ReturnType<typeof setTimeout>;
}

interface ReadEventData {
  messageId: number;
  roomId: number;
  reader: { type: 'user' | 'admin'; id: number; firstName: string | null; username: string | null };
  readAt: string;
}

type ChatEvent =
  | { kind: 'new_message'; data: WsNewMessage }
  | { kind: 'edit'; data: { messageId: number; roomId: number; content: string; editedAt: string } }
  | { kind: 'delete'; data: { messageId: number; roomId: number } }
  | { kind: 'read'; data: ReadEventData };

// ─── Helpers ─────────────────────────────────────────────────────────────────

const API_URL = resolveApiUrl();

function getAuthHeaders(): HeadersInit {
  return {
    'Content-Type': 'application/json',
  };
}

function getUserFromRoom(room: ChatRoom): RoomParticipantUser | null {
  const participant = room.participants.find((p) => p.userId !== null && p.user !== null);
  return participant?.user ?? null;
}

function getUserDisplayName(user: RoomParticipantUser | null): string {
  if (!user) { return 'Пользователь'; }
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ');
  return name || user.username || 'Пользователь';
}

function getAvatarInitial(user: RoomParticipantUser | null): string {
  if (!user) { return 'П'; }
  if (user.firstName) { return user.firstName.charAt(0).toUpperCase(); }
  if (user.username) { return user.username.charAt(0).toUpperCase(); }
  return 'П';
}

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();

  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterdayStart = new Date(todayStart.getTime() - 86400000);

  if (date >= todayStart) {
    const diffMs = now.getTime() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) { return 'сейчас'; }
    if (diffMin < 60) { return `${diffMin} мин`; }
    const diffHours = Math.floor(diffMin / 60);
    return `${diffHours} ч`;
  }

  if (date >= yesterdayStart) {
    return 'вчера';
  }

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${day}.${month}`;
}

function formatDateSeparator(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();

  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterdayStart = new Date(todayStart.getTime() - 86400000);

  if (date >= todayStart) { return 'Сегодня'; }
  if (date >= yesterdayStart) { return 'Вчера'; }

  return date.toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
}

function groupMessagesByDate(messages: ChatMessage[]): Array<{ dateKey: string; label: string; messages: ChatMessage[] }> {
  const groups: Array<{ dateKey: string; label: string; messages: ChatMessage[] }> = [];
  let currentDateKey = '';

  for (const msg of messages) {
    const dateKey = new Date(msg.createdAt).toDateString();
    if (dateKey !== currentDateKey) {
      currentDateKey = dateKey;
      groups.push({
        dateKey,
        label: formatDateSeparator(msg.createdAt),
        messages: [msg],
      });
    } else {
      const lastGroup = groups.at(-1);
      if (lastGroup) {
        lastGroup.messages.push(msg);
      }
    }
  }

  return groups;
}

// ─── NewChatDialog ───────────────────────────────────────────────────────────

function NewChatDialog({
  open,
  onOpenChange,
  onUserSelected,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUserSelected: (userId: number) => void;
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchUserResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const searchUsers = useCallback(async (searchQuery: string) => {
    if (searchQuery.trim().length < 2) {
      setResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const response = await fetch(
        `${API_URL}/users?search=${encodeURIComponent(searchQuery)}&limit=10`,
        { headers: getAuthHeaders(), credentials: 'include' },
      );
      if (response.ok) {
        const json = await response.json();
        const data = json.data ?? [];
        setResults(
          data.map((u: SearchUserResult) => ({
            id: u.id,
            firstName: u.firstName ?? null,
            lastName: u.lastName ?? null,
            username: u.username ?? null,
            photoUrl: u.photoUrl ?? null,
          })),
        );
      }
    } catch {
      // ignore search errors
    } finally {
      setIsSearching(false);
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) { clearTimeout(debounceRef.current); }

    debounceRef.current = setTimeout(() => {
      searchUsers(query);
    }, 300);

    return () => {
      if (debounceRef.current) { clearTimeout(debounceRef.current); }
    };
  }, [query, searchUsers]);

  useEffect(() => {
    if (!open) {
      setQuery('');
      setResults([]);
    }
  }, [open]);

  const handleSelect = (userId: number) => {
    onUserSelected(userId);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Новый чат</DialogTitle>
          <DialogDescription>Найдите пользователя для начала переписки</DialogDescription>
        </DialogHeader>

        <Input
          placeholder="Имя или username..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />

        <div className="max-h-60 overflow-y-auto">
          {isSearching && (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          )}

          {!isSearching && query.trim().length >= 2 && results.length === 0 && (
            <p className="py-4 text-center text-sm text-muted-foreground">
              Пользователи не найдены
            </p>
          )}

          {results.map((user) => {
            const displayName = [user.firstName, user.lastName].filter(Boolean).join(' ') || user.username || 'Пользователь';
            return (
              <button
                key={user.id}
                onClick={() => handleSelect(user.id)}
                className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-left transition-colors hover:bg-muted"
              >
                <Avatar className="h-8 w-8">
                  {user.photoUrl && <AvatarImage src={user.photoUrl} />}
                  <AvatarFallback className="bg-primary/10 text-xs">
                    {displayName.charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{displayName}</p>
                  {user.username && (
                    <p className="truncate text-xs text-muted-foreground">@{user.username}</p>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── ChatRoomItem ────────────────────────────────────────────────────────────

function ChatRoomItem({
  room,
  isActive,
  onClick,
}: {
  room: ChatRoom;
  isActive: boolean;
  onClick: () => void;
}) {
  const user = getUserFromRoom(room);
  const displayName = getUserDisplayName(user);
  const initial = getAvatarInitial(user);

  const lastMessagePreview = useMemo(() => {
    if (!room.lastMessage) { return null; }
    const isAdmin = room.lastMessage.sender?.type === 'admin';
    const prefix = isAdmin ? 'Вы: ' : '';
    const content = room.lastMessage.content || '\u{1F4CE} Вложение';
    return `${prefix}${content}`;
  }, [room.lastMessage]);

  return (
    <button
      onClick={onClick}
      className={cn(
        'flex w-full max-w-full min-w-0 overflow-hidden items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/50',
        isActive && 'bg-muted',
      )}
    >
      <Avatar className="h-10 w-10 shrink-0">
        {user?.photoUrl && <AvatarImage src={user.photoUrl} />}
        <AvatarFallback className="bg-primary/10 text-sm font-medium">
          {initial}
        </AvatarFallback>
      </Avatar>

      <div className="min-w-0 flex-1 overflow-hidden">
        <div className="flex min-w-0 items-center gap-2">
          <span className="min-w-0 flex-1 truncate text-sm font-medium">{displayName}</span>
          {room.lastMessageAt && (
            <span className="ml-auto shrink-0 whitespace-nowrap text-[11px] text-muted-foreground">
              {formatRelativeTime(room.lastMessageAt)}
            </span>
          )}
        </div>

        <div className="mt-0.5 flex min-w-0 items-center gap-2">
          {lastMessagePreview ? (
            <p className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
              {lastMessagePreview}
            </p>
          ) : (
            <span className="min-w-0 flex-1" />
          )}
          {room.unreadCount > 0 && (
            <Badge className="h-5 min-w-5 shrink-0 justify-center px-1.5 text-[10px]">
              {room.unreadCount}
            </Badge>
          )}
        </div>
      </div>
    </button>
  );
}

// ─── MessageBubble ───────────────────────────────────────────────────────────

function MessageBubble({ message }: { message: ChatMessage }) {
  return <AdminMessageBubble message={message} />;
}

// ─── AttachmentPreviewChip ──────────────────────────────────────────────────

function AttachmentTypeIcon({ type }: { type: 'image' | 'video' | 'audio' | 'document' }) {
  switch (type) {
    case 'image':
      return <ImageIcon className="h-5 w-5" />;
    case 'video':
      return <Film className="h-5 w-5" />;
    case 'audio':
      return <Music className="h-5 w-5" />;
    default:
      return <FileText className="h-5 w-5" />;
  }
}

function AttachmentPreviewChip({
  attachment,
  onRemove,
}: {
  attachment: PendingAttachment;
  onRemove: () => void;
}) {
  const fileType = getFileTypeFromMime(attachment.file.type);

  return (
    <div className="relative flex max-w-[220px] min-w-0 items-center gap-2 rounded-lg border bg-muted/40 px-3 py-2">
      <button
        type="button"
        onClick={onRemove}
        className="absolute -right-1.5 -top-1.5 z-10 flex h-5 w-5 items-center justify-center rounded-full bg-background text-muted-foreground transition-colors hover:text-destructive"
        title="Убрать файл"
      >
        <X className="h-3 w-3" />
      </button>

      {attachment.preview ? (
        <img
          src={attachment.preview}
          alt={attachment.file.name}
          className="h-10 w-10 shrink-0 rounded object-cover"
        />
      ) : (
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded bg-background text-muted-foreground">
          <AttachmentTypeIcon type={fileType} />
        </div>
      )}

      <div className="min-w-0 flex-1">
        <p className="truncate text-[11px] font-medium">{attachment.file.name}</p>
        <p className="text-[10px] text-muted-foreground">
          {formatFileSize(attachment.file.size)}
        </p>
      </div>

      {attachment.isUploading && (
        <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" />
      )}
      {attachment.error && (
        <span
          className="shrink-0 text-[10px] text-destructive"
          title={attachment.error}
        >
          {attachment.error}
        </span>
      )}
    </div>
  );
}

// ─── ChatPanel ───────────────────────────────────────────────────────────────

function ChatPanel({
  roomId,
  room,
  ws,
  typingUserName,
  chatEventsRef,
  chatEventsVersion,
  adminId,
}: {
  roomId: number;
  room: ChatRoom | null;
  ws: ReturnType<typeof useAdminChatWs>;
  typingUserName: string | null;
  chatEventsRef: React.RefObject<ChatEvent[]>;
  chatEventsVersion: number;
  adminId: number | undefined;
}) {
  const navigate = useNavigate();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoadingMessages, setIsLoadingMessages] = useState(true);
  const [messageText, setMessageText] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [pendingAttachments, setPendingAttachments] = useState<PendingAttachment[]>([]);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastTypingSentRef = useRef(0);
  const lastMarkedReadIdRef = useRef<number | null>(null);

  const user = room ? getUserFromRoom(room) : null;
  const displayName = getUserDisplayName(user);

  // Загрузка сообщений
  const fetchMessages = useCallback(async () => {
    try {
      const response = await fetch(
        `${API_URL}/admin/messaging/rooms/${roomId}/messages?limit=50`,
        { headers: getAuthHeaders() },
      );
      if (response.ok) {
        const json = await response.json();
        const data: ChatMessage[] = json.data ?? [];
        // API возвращает в обратном хронологическом порядке (новые первые)
        // Сортируем по createdAt ASC — старые сверху, новые снизу
        const sorted = [...data].sort(
          (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
        );
        setMessages(sorted);
      }
    } catch {
      // ignore
    } finally {
      setIsLoadingMessages(false);
    }
  }, [roomId]);

  useEffect(() => {
    setMessages([]);
    setIsLoadingMessages(true);
    setMessageText('');
    lastMarkedReadIdRef.current = null;
    // Освобождаем blob-URL'ы превью неотправленных файлов перед сбросом
    setPendingAttachments((prev) => {
      for (const a of prev) {
        if (a.preview) { URL.revokeObjectURL(a.preview); }
      }
      return [];
    });
    fetchMessages();
  }, [roomId, fetchMessages]);

  // Cleanup blob URLs при unmount всего ChatPanel
  useEffect(() => {
    return () => {
      setPendingAttachments((prev) => {
        for (const a of prev) {
          if (a.preview) { URL.revokeObjectURL(a.preview); }
        }
        return [];
      });
    };
  }, []);

  // Подписка на WS комнату (переподписка при реконнекте через connectionId)
  useEffect(() => {
    if (ws.isConnected) {
      ws.subscribe(roomId);
    }
    return () => {
      if (ws.isConnected) {
        ws.unsubscribe(roomId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- ws.connectionId triggers re-subscribe on reconnect
  }, [roomId, ws.isConnected, ws.connectionId]);

  // Отметить прочитанными через WS при любом новом последнем сообщении
  // (даже своём — чтобы обновить lastReadMessageId и не считать чужие сообщения выше непрочитанными)
  useEffect(() => {
    if (messages.length === 0 || !ws.isConnected) { return; }
    const lastMsg = messages.at(-1);
    if (!lastMsg) { return; }
    if (lastMsg.id === lastMarkedReadIdRef.current) { return; }
    lastMarkedReadIdRef.current = lastMsg.id;
    ws.markRead(roomId, lastMsg.id);
  }, [messages, roomId, ws]);

  // Автоскролл при новых сообщениях
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      // Скролл вниз через requestAnimationFrame для надёжности
      requestAnimationFrame(() => {
        container.scrollTop = container.scrollHeight;
      });
    }
  }, [messages]);

  // Обработка всех WS-событий из event queue
  useEffect(() => {
    const events = chatEventsRef.current.splice(0);
    for (const event of events) {
      switch (event.kind) {
        case 'new_message': {
          const wsMsg = event.data;
          if (wsMsg.chatRoomId !== roomId) { break; }
          setMessages((prev) => {
            if (prev.some((m) => m.id === wsMsg.id)) { return prev; }
            const newMsg: ChatMessage = {
              id: wsMsg.id,
              chatRoomId: wsMsg.chatRoomId,
              content: wsMsg.content,
              isSystemMessage: wsMsg.isSystemMessage,
              replyToMessageId: wsMsg.replyToMessageId,
              status: wsMsg.status,
              isEdited: wsMsg.isEdited,
              editedAt: wsMsg.editedAt,
              isDeleted: wsMsg.isDeleted,
              createdAt: wsMsg.createdAt,
              sender: wsMsg.sender,
              replyTo: null,
              reactions: wsMsg.reactions,
              readBy: wsMsg.readBy,
              attachments: wsMsg.attachments ?? [],
            };
            // Заменяем optimistic сообщение (отрицательный ID, тот же контент, от админа)
            const optimisticIdx = prev.findIndex(
              (m) => m.id < 0 && m.content === wsMsg.content && m.sender?.type === 'admin',
            );
            if (optimisticIdx !== -1) {
              const updated = [...prev];
              updated[optimisticIdx] = newMsg;
              return updated;
            }
            const updated = [...prev, newMsg];
            updated.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
            return updated;
          });
          break;
        }
        case 'edit': {
          if (event.data.roomId !== roomId) { break; }
          const editData = event.data;
          setMessages((prev) =>
            prev.map((m) =>
              m.id === editData.messageId
                ? { ...m, content: editData.content, isEdited: true, editedAt: editData.editedAt }
                : m,
            ),
          );
          break;
        }
        case 'delete': {
          if (event.data.roomId !== roomId) { break; }
          const deleteData = event.data;
          setMessages((prev) =>
            prev.map((m) =>
              m.id === deleteData.messageId
                ? { ...m, isDeleted: true, content: '' }
                : m,
            ),
          );
          break;
        }
        case 'read': {
          if (event.data.roomId !== roomId) { break; }
          const readData = event.data;
          setMessages((prev) =>
            prev.map((m) => {
              if (m.id !== readData.messageId) { return m; }
              const alreadyRead = m.readBy.some(
                (r) => r.type === readData.reader.type && r.id === readData.reader.id,
              );
              if (alreadyRead) { return m; }
              return {
                ...m,
                status: MessageDeliveryStatus.READ,
                readBy: [...m.readBy, {
                  type: readData.reader.type,
                  id: readData.reader.id,
                  firstName: readData.reader.firstName,
                  username: readData.reader.username,
                  readAt: readData.readAt,
                }],
              };
            }),
          );
          break;
        }
      }
    }
  }, [chatEventsVersion, roomId, chatEventsRef]);

  // Загрузка файла на бэк
  const uploadAttachment = useCallback(async (file: File, tempId: string) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await fetch(`${API_URL}/admin/messaging/rooms/${roomId}/upload`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      if (!res.ok) { throw new Error('upload failed'); }
      const json: ChatUploadResponse = await res.json();
      if (!json.data || typeof json.data.id !== 'number') {
        throw new Error('no file id');
      }
      const fileId = json.data.id;
      setPendingAttachments((prev) =>
        prev.map((a) =>
          a.tempId === tempId ? { ...a, isUploading: false, uploadedFileId: fileId } : a,
        ),
      );
    } catch {
      setPendingAttachments((prev) =>
        prev.map((a) =>
          a.tempId === tempId
            ? { ...a, isUploading: false, error: 'Ошибка загрузки' }
            : a,
        ),
      );
    }
  }, [roomId]);

  const handleFileSelect = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) { return; }

    const remaining = MAX_ATTACHMENTS - pendingAttachments.length;
    const filesToAdd = [...files].slice(0, remaining);

    for (const file of filesToAdd) {
      if (file.size > MAX_CHAT_FILE_SIZE) {
        setPendingAttachments((prev) => [
          ...prev,
          {
            tempId: generateTempId(),
            file,
            preview: null,
            uploadedFileId: null,
            isUploading: false,
            error: `Макс. ${MAX_CHAT_FILE_SIZE / 1024 / 1024}MB`,
          },
        ]);
        continue;
      }

      const tempId = generateTempId();
      const fileType = getFileTypeFromMime(file.type);
      const preview = fileType === 'image' ? URL.createObjectURL(file) : null;

      setPendingAttachments((prev) => [
        ...prev,
        {
          tempId,
          file,
          preview,
          uploadedFileId: null,
          isUploading: true,
          error: null,
        },
      ]);

      void uploadAttachment(file, tempId);
    }

    e.target.value = '';
  }, [pendingAttachments.length, uploadAttachment]);

  const removeAttachment = useCallback((tempId: string) => {
    setPendingAttachments((prev) => {
      const a = prev.find((x) => x.tempId === tempId);
      if (a?.preview) { URL.revokeObjectURL(a.preview); }
      return prev.filter((x) => x.tempId !== tempId);
    });
  }, []);

  const openFilePicker = useCallback(() => {
    if (pendingAttachments.length >= MAX_ATTACHMENTS) { return; }
    fileInputRef.current?.click();
  }, [pendingAttachments.length]);

  // Отправка
  const handleSend = async () => {
    const content = messageText.trim();
    const uploadedFileIds: number[] = [];
    for (const a of pendingAttachments) {
      if (a.uploadedFileId !== null && !a.error) {
        uploadedFileIds.push(a.uploadedFileId);
      }
    }
    const hasUploading = pendingAttachments.some((a) => a.isUploading);

    if (isSending) { return; }
    if (hasUploading) { return; }
    if (!content && uploadedFileIds.length === 0) { return; }

    setIsSending(true);
    setMessageText('');

    // Optimistic вложения: показываем превью пока WS не пришлёт реальные
    const optimisticAttachments: ChatMessageAttachment[] = pendingAttachments
      .filter((a) => a.uploadedFileId !== null && !a.error)
      .map((a, idx) => ({
        id: -(Date.now() + idx),
        fileId: a.uploadedFileId ?? 0,
        type: getFileTypeFromMime(a.file.type),
        url: a.preview ?? '',
        name: a.file.name,
        size: a.file.size,
        mimeType: a.file.type,
      }));

    const optimisticId = -(Date.now());
    const optimisticMessage: ChatMessage = {
      id: optimisticId,
      chatRoomId: roomId,
      content,
      isSystemMessage: false,
      replyToMessageId: null,
      status: MessageDeliveryStatus.SENT,
      isEdited: false,
      editedAt: null,
      isDeleted: false,
      createdAt: new Date().toISOString(),
      sender: {
        type: 'admin',
        id: adminId ?? 0,
        firstName: null,
        lastName: null,
        username: null,
      },
      replyTo: null,
      reactions: [],
      readBy: [],
      attachments: optimisticAttachments,
    };
    setMessages((prev) => [...prev, optimisticMessage]);

    const previewsToRevoke = pendingAttachments
      .map((a) => a.preview)
      .filter((p): p is string => p !== null);
    setPendingAttachments([]);

    try {
      if (ws.isConnected) {
        ws.sendMessage(
          roomId,
          content,
          undefined,
          uploadedFileIds.length > 0 ? uploadedFileIds : undefined,
        );
      } else {
        await fetch(`${API_URL}/admin/messaging/rooms/${roomId}/messages`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            content: content || undefined,
            attachmentFileIds: uploadedFileIds.length > 0 ? uploadedFileIds : undefined,
          }),
        });
        await fetchMessages();
      }
      setTimeout(() => {
        for (const url of previewsToRevoke) {
          URL.revokeObjectURL(url);
        }
      }, 5000);
    } catch {
      setMessages((prev) => prev.filter((m) => m.id !== optimisticId));
      setMessageText(content);
    } finally {
      setIsSending(false);
      textareaRef.current?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessageText(e.target.value);

    // Typing indicator: отправляем не чаще раза в 3 секунды
    const now = Date.now();
    if (now - lastTypingSentRef.current > 3000) {
      ws.startTyping(roomId);
      lastTypingSentRef.current = now;
    }

    if (typingTimeoutRef.current) { clearTimeout(typingTimeoutRef.current); }
    typingTimeoutRef.current = setTimeout(() => {
      ws.stopTyping(roomId);
    }, 3000);
  };

  const userId = user?.id;
  const grouped = groupMessagesByDate(messages);

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex items-center justify-between border-b px-4 py-3">
        <div className="flex items-center gap-3">
          <Avatar className="h-8 w-8">
            {user?.photoUrl && <AvatarImage src={user.photoUrl} />}
            <AvatarFallback className="bg-primary/10 text-xs">
              {getAvatarInitial(user)}
            </AvatarFallback>
          </Avatar>
          <div>
            <p className="text-sm font-medium">{displayName}</p>
            {user?.username && (
              <p className="text-xs text-muted-foreground">@{user.username}</p>
            )}
          </div>
        </div>

        {userId && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/users/show/${userId}`)}
          >
            <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
            Профиль
          </Button>
        )}
      </div>

      {/* Messages */}
      {isLoadingMessages ? (
        <div className="flex flex-1 items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div ref={scrollContainerRef} className="flex-1 overflow-y-auto px-4">
          <div className="space-y-1 py-4">
            {messages.length === 0 ? (
              <div className="flex h-full items-center justify-center py-20">
                <div className="text-center text-sm text-muted-foreground">
                  <MessageSquare className="mx-auto mb-2 h-8 w-8 opacity-50" />
                  <p>Нет сообщений</p>
                  <p className="text-xs">Напишите первое сообщение</p>
                </div>
              </div>
            ) : (
              grouped.map((group) => (
                <div key={group.dateKey}>
                  <div className="my-3 flex items-center justify-center">
                    <span className="rounded-full bg-muted px-3 py-1 text-xs text-muted-foreground">
                      {group.label}
                    </span>
                  </div>
                  {group.messages.map((msg) => (
                    <MessageBubble key={msg.id} message={msg} />
                  ))}
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Typing indicator */}
      {typingUserName && (
        <div className="px-4 pb-1">
          <p className="text-xs text-muted-foreground">
            {typingUserName} печатает
            <span className="inline-flex w-6">
              <span className="animate-pulse">...</span>
            </span>
          </p>
        </div>
      )}

      {/* Input */}
      <div className="border-t px-4 py-3">
        {/* Pending attachments preview */}
        {pendingAttachments.length > 0 && (
          <div className="mb-2 flex flex-wrap gap-2">
            {pendingAttachments.map((attachment) => (
              <AttachmentPreviewChip
                key={attachment.tempId}
                attachment={attachment}
                onRemove={() => removeAttachment(attachment.tempId)}
              />
            ))}
          </div>
        )}

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED_FILE_TYPES}
          multiple
          onChange={handleFileSelect}
          className="hidden"
        />

        <div className="flex items-end gap-2">
          <Button
            type="button"
            size="icon"
            variant="ghost"
            onClick={openFilePicker}
            disabled={pendingAttachments.length >= MAX_ATTACHMENTS}
            className="shrink-0"
            title={
              pendingAttachments.length >= MAX_ATTACHMENTS
                ? `Не более ${MAX_ATTACHMENTS} файлов`
                : 'Прикрепить файлы'
            }
          >
            <Paperclip className="h-4 w-4" />
          </Button>
          <Textarea
            ref={textareaRef}
            value={messageText}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder="Написать сообщение..."
            className="min-h-[40px] max-h-[120px] resize-none text-sm"
            rows={1}
          />
          <Button
            size="icon"
            onClick={handleSend}
            disabled={
              isSending
              || pendingAttachments.some((a) => a.isUploading)
              || (
                !messageText.trim()
                && !pendingAttachments.some((a) => a.uploadedFileId !== null && !a.error)
              )
            }
            className="shrink-0"
          >
            {isSending || pendingAttachments.some((a) => a.isUploading) ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── MessagingPage ───────────────────────────────────────────────────────────

export function MessagingPage() {
  useScrollRestoration();
  const { data: identity } = useGetIdentity<{ id: number }>();
  const [rooms, setRooms] = useState<ChatRoom[]>([]);
  const [isLoadingRooms, setIsLoadingRooms] = useState(true);
  const [activeRoomId, setActiveRoomId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [newChatOpen, setNewChatOpen] = useState(false);
  const [typingStates, setTypingStates] = useState<TypingState[]>([]);
  const typingStatesRef = useRef<TypingState[]>([]);
  const chatEventsRef = useRef<ChatEvent[]>([]);
  const [chatEventsVersion, setChatEventsVersion] = useState(0);

  // Синхронизируем ref с state
  useEffect(() => {
    typingStatesRef.current = typingStates;
  }, [typingStates]);

  // ─── Fetch rooms ───────────────────────────────────────────────────────

  const fetchRooms = useCallback(async () => {
    try {
      const response = await fetch(
        `${API_URL}/admin/messaging/rooms?limit=100`,
        { headers: getAuthHeaders() },
      );
      if (response.ok) {
        const json = await response.json();
        setRooms(json.data ?? []);
      }
    } catch {
      // ignore
    } finally {
      setIsLoadingRooms(false);
    }
  }, []);

  useEffect(() => {
    fetchRooms();
  }, [fetchRooms]);

  // ─── WebSocket ─────────────────────────────────────────────────────────

  const pushChatEvent = useCallback((event: ChatEvent) => {
    chatEventsRef.current.push(event);
    setChatEventsVersion((v) => v + 1);
  }, []);

  const handleWsNewMessage = useCallback((msg: WsNewMessage) => {
    // Проксируем в ChatPanel через event queue
    pushChatEvent({ kind: 'new_message', data: msg });

    // Обновляем lastMessage и unreadCount в списке комнат
    setRooms((prev) => {
      const updated = prev.map((room) => {
        if (room.id !== msg.chatRoomId) { return room; }
        return {
          ...room,
          lastMessage: {
            id: msg.id,
            content: msg.content,
            createdAt: msg.createdAt,
            sender: msg.sender,
          },
          lastMessageAt: msg.createdAt,
          // Своё сообщение или активный чат → обнуляем unread, чужое → +1
          unreadCount: (room.id === activeRoomId || (msg.sender?.type === 'admin' && msg.sender.id === identity?.id))
            ? 0
            : room.unreadCount + 1,
        } satisfies ChatRoom;
      });

      // Если комнаты нет в списке — перезагружаем
      const found = updated.some((r) => r.id === msg.chatRoomId);
      if (!found) {
        fetchRooms();
        return prev;
      }

      // Сортируем по lastMessageAt desc
      return updated.sort((a, b) => {
        const aTime = a.lastMessageAt ? new Date(a.lastMessageAt).getTime() : 0;
        const bTime = b.lastMessageAt ? new Date(b.lastMessageAt).getTime() : 0;
        return bTime - aTime;
      });
    });
  }, [activeRoomId, fetchRooms, identity?.id, pushChatEvent]);

  const handleWsRoomUpdate = useCallback((data: WsRoomUpdateData) => {
    setRooms((prev) => {
      const updated = prev.map((room) => {
        if (room.id !== data.roomId) { return room; }
        // Не обновляем если сообщение уже учтено (дедупликация с handleWsNewMessage)
        if (room.lastMessage && room.lastMessage.id >= data.lastMessage.id) { return room; }
        return {
          ...room,
          lastMessage: {
            id: data.lastMessage.id,
            content: data.lastMessage.content,
            createdAt: data.lastMessage.createdAt,
            sender: data.lastMessage.sender,
          },
          lastMessageAt: data.lastMessageAt,
          // Своё сообщение или активный чат → обнуляем unread, чужое → +1
          unreadCount: (room.id === activeRoomId || (data.lastMessage.sender?.type === 'admin' && data.lastMessage.sender.id === identity?.id))
            ? 0
            : room.unreadCount + 1,
        } satisfies ChatRoom;
      });

      // Если комнаты нет в списке — перезагружаем
      const found = updated.some((r) => r.id === data.roomId);
      if (!found) {
        fetchRooms();
        return prev;
      }

      // Сортируем по lastMessageAt desc
      return updated.sort((a, b) => {
        const aTime = a.lastMessageAt ? new Date(a.lastMessageAt).getTime() : 0;
        const bTime = b.lastMessageAt ? new Date(b.lastMessageAt).getTime() : 0;
        return bTime - aTime;
      });
    });
  }, [activeRoomId, fetchRooms, identity?.id]);

  const handleWsTyping = useCallback(({ roomId, user }: { roomId: number; user: { type: 'user' | 'admin'; id: number; firstName: string | null; username: string | null } }) => {
    // Нас не интересует typing от других админов
    if (user.type === 'admin') { return; }

    const userName = user.firstName ?? user.username ?? 'Пользователь';

    setTypingStates((prev) => {
      // Удаляем старый таймер для этой комнаты
      const existing = prev.find((t) => t.roomId === roomId);
      if (existing) {
        clearTimeout(existing.timeoutId);
      }

      const timeoutId = setTimeout(() => {
        setTypingStates((p) => p.filter((t) => t.roomId !== roomId));
      }, 5000);

      const filtered = prev.filter((t) => t.roomId !== roomId);
      return [...filtered, { roomId, userName, timeoutId }];
    });
  }, []);

  const handleWsStoppedTyping = useCallback(({ roomId }: { roomId: number; userId: number; userType: 'user' | 'admin' }) => {
    setTypingStates((prev) => {
      const existing = prev.find((t) => t.roomId === roomId);
      if (existing) {
        clearTimeout(existing.timeoutId);
      }
      return prev.filter((t) => t.roomId !== roomId);
    });
  }, []);

  const handleWsMessageRead = useCallback((data: ReadEventData) => {
    // Проксируем в ChatPanel через event queue
    pushChatEvent({ kind: 'read', data });

    // Обнуляем unread если читаем мы
    if (data.roomId === activeRoomId) {
      setRooms((prev) =>
        prev.map((room) =>
          room.id === data.roomId ? { ...room, unreadCount: 0 } : room,
        ),
      );
    }
  }, [activeRoomId, pushChatEvent]);

  const handleWsMessageEdited = useCallback((data: { messageId: number; roomId: number; content: string; editedAt: string }) => {
    pushChatEvent({ kind: 'edit', data });
  }, [pushChatEvent]);

  const handleWsMessageDeleted = useCallback((data: { messageId: number; roomId: number }) => {
    pushChatEvent({ kind: 'delete', data });
  }, [pushChatEvent]);

  const ws = useAdminChatWs({
    onNewMessage: handleWsNewMessage,
    onMessageEdited: handleWsMessageEdited,
    onMessageDeleted: handleWsMessageDeleted,
    onTyping: handleWsTyping,
    onStoppedTyping: handleWsStoppedTyping,
    onMessageRead: handleWsMessageRead,
    onRoomUpdate: handleWsRoomUpdate,
  });

  // ─── Активный чат ──────────────────────────────────────────────────────

  const handleSelectRoom = useCallback((roomId: number) => {
    setActiveRoomId(roomId);

    // Обнуляем unread при выборе чата
    setRooms((prev) =>
      prev.map((room) =>
        room.id === roomId ? { ...room, unreadCount: 0 } : room,
      ),
    );
  }, []);

  // ─── Новый чат ─────────────────────────────────────────────────────────

  const handleNewChat = useCallback(async (userId: number) => {
    try {
      const response = await fetch(
        `${API_URL}/admin/messaging/user/${userId}/chat`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
        },
      );
      if (response.ok) {
        const json = await response.json();
        const newRoom = json.data;
        if (newRoom?.id) {
          // Перезагружаем список и выбираем новый чат
          await fetchRooms();
          setActiveRoomId(newRoom.id);
        }
      }
    } catch {
      // ignore
    }
  }, [fetchRooms]);

  // ─── Фильтрация ───────────────────────────────────────────────────────

  const filteredRooms = useMemo(() => {
    const sorted = [...rooms].sort((a, b) => {
      const aTime = a.lastMessageAt ? new Date(a.lastMessageAt).getTime() : 0;
      const bTime = b.lastMessageAt ? new Date(b.lastMessageAt).getTime() : 0;
      return bTime - aTime;
    });

    if (!searchQuery.trim()) { return sorted; }

    const q = searchQuery.toLowerCase();
    return sorted.filter((room) => {
      const user = getUserFromRoom(room);
      if (!user) { return false; }
      const name = getUserDisplayName(user).toLowerCase();
      const username = (user.username ?? '').toLowerCase();
      return name.includes(q) || username.includes(q);
    });
  }, [rooms, searchQuery]);

  const activeRoom = useMemo(
    () => rooms.find((r) => r.id === activeRoomId) ?? null,
    [rooms, activeRoomId],
  );

  const activeTypingUserName = useMemo(
    () => typingStates.find((t) => t.roomId === activeRoomId)?.userName ?? null,
    [typingStates, activeRoomId],
  );

  // ─── Render ────────────────────────────────────────────────────────────

  return (
    <div className="flex h-[calc(100vh-100px)]">
      {/* Левая панель — список чатов */}
      <div className="flex w-80 shrink-0 overflow-hidden flex-col border-r">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-muted-foreground" />
            <h2 className="text-sm font-semibold">Сообщения</h2>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => setNewChatOpen(true)}
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>

        <Separator />

        {/* Поиск */}
        <div className="px-3 py-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Поиск чата..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-8 pl-8 text-sm"
            />
          </div>
        </div>

        {/* Список чатов */}
        <div className="min-w-0 flex-1 overflow-y-auto overflow-x-hidden">
          {isLoadingRooms ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            filteredRooms.length === 0 ? (
              <div className="px-4 py-10 text-center text-sm text-muted-foreground">
                {searchQuery.trim() ? 'Чаты не найдены' : 'Нет активных чатов'}
              </div>
            ) : (
              <div className="divide-y">
                {filteredRooms.map((room) => (
                  <ChatRoomItem
                    key={room.id}
                    room={room}
                    isActive={room.id === activeRoomId}
                    onClick={() => handleSelectRoom(room.id)}
                  />
                ))}
              </div>
            )
          )}
        </div>
      </div>

      {/* Правая панель — чат */}
      <div className="flex flex-1 flex-col">
        {activeRoomId ? (
          <ChatPanel
            key={activeRoomId}
            roomId={activeRoomId}
            room={activeRoom}
            ws={ws}
            typingUserName={activeTypingUserName}
            chatEventsRef={chatEventsRef}
            chatEventsVersion={chatEventsVersion}
            adminId={identity?.id}
          />
        ) : (
          <div className="flex flex-1 items-center justify-center">
            <div className="text-center text-muted-foreground">
              <MessageSquare className="mx-auto mb-3 h-12 w-12 opacity-30" />
              <p className="text-sm font-medium">Выберите чат</p>
              <p className="mt-1 text-xs">Или начните новую переписку</p>
            </div>
          </div>
        )}
      </div>

      {/* Диалог нового чата */}
      <NewChatDialog
        open={newChatOpen}
        onOpenChange={setNewChatOpen}
        onUserSelected={handleNewChat}
      />
    </div>
  );
}
